import { useEffect, useState } from 'react';
import HammMedia from '../app/services/hammurabi/media';

// Files in SB's S3 bucket are private: they are shown through short-lived signed URLs from the backend.
// public/ and URLs outside S3 (e.g. Google avatars) are used as they are.
const cache = new Map(); // url -> { signed, expiresAt }
const inFlight = new Map(); // url -> Promise

export const needsSigning = url =>
	typeof url === 'string' && /\.amazonaws\.com\//.test(url) && !/\.amazonaws\.com\/public\//.test(url);

export const getSignedUrl = async url => {
	if (!needsSigning(url)) return url;
	const cached = cache.get(url);
	if (cached && cached.expiresAt > Date.now()) return cached.signed;
	if (inFlight.has(url)) return inFlight.get(url);

	const request = HammMedia.sign([url])
		.then(({ data, expires_in: expiresIn }) => {
			const signed = data?.[0]?.signed_url || null;
			if (signed) {
				// refresh a minute before it expires
				cache.set(url, { signed, expiresAt: Date.now() + Math.max((expiresIn || 600) - 60, 30) * 1000 });
			}
			return signed;
		})
		.finally(() => inFlight.delete(url));
	inFlight.set(url, request);
	return request;
};

// Opens a file in a new tab with a fresh signed URL (for links and buttons).
export const openSigned = async url => {
	const signed = await getSignedUrl(url);
	if (signed) window.open(signed, '_blank', 'noopener,noreferrer');
};

const useSignedUrl = url => {
	const [signed, setSigned] = useState(needsSigning(url) ? null : url);

	useEffect(() => {
		let alive = true;
		if (!needsSigning(url)) {
			setSigned(url);
			return undefined;
		}
		setSigned(null);
		getSignedUrl(url)
			.then(value => alive && setSigned(value))
			.catch(() => alive && setSigned(null));
		return () => {
			alive = false;
		};
	}, [url]);

	return signed;
};

export default useSignedUrl;
